import React, { useEffect, useState, useRef } from "react";
import {
  Heart, FileText, CalendarCheck, BellRinging, UploadSimple, CheckCircle, Clock,
  Buildings, TrendUp, Briefcase, Trash, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../i18n";
import PropertyCard from "../../components/PropertyCard";
import api from "../../lib/api";
import { toast } from "sonner";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatMAD } from "../../lib/format";

const PROJECTS = [
  { key: "financing", icon: Buildings, tk: "fin" },
  { key: "placement", icon: TrendUp, tk: "plc" },
  { key: "implantation", icon: Briefcase, tk: "imp" },
];

const InvestorDashboard = () => {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [favorites, setFavorites] = useState([]);
  const [dossier, setDossier] = useState(null);
  const [slots, setSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const fileRefs = useRef({});

  const load = () => {
    api.get("/favorites").then(({ data }) => setFavorites(data)).catch(() => {});
    api.get("/dossier").then(({ data }) => setDossier(data)).catch(() => {});
    api.get("/slots").then(({ data }) => setSlots(data)).catch(() => {});
    api.get("/appointments").then(({ data }) => setAppointments(data)).catch(() => {});
    api.get("/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const chooseProject = async (type) => {
    const { data } = await api.post("/dossier", { project_type: type });
    setDossier(data);
    toast.success("Projet enregistré");
  };

  const uploadDoc = async (docKey, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post("/dossier/document", { doc_key: docKey, file_id: data.file_id, filename: data.filename });
      setDossier(res.data);
      toast.success("Document ajouté");
    } catch {
      toast.error("Échec du téléversement");
    }
  };

  const submitDossier = async () => {
    try {
      const { data } = await api.post("/dossier/submit");
      setDossier(data);
      toast.success("Dossier déposé en banque !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const book = async (slot, subject) => {
    try {
      await api.post("/appointments", { slot_id: slot.id, subject, notes: "" });
      toast.success("Rendez-vous confirmé");
      load();
    } catch { toast.error("Créneau indisponible"); }
  };

  const docsUploaded = dossier?.documents?.filter((d) => d.file_id).length || 0;
  const docsTotal = dossier?.documents?.length || 0;
  const progress = docsTotal ? Math.round((docsUploaded / docsTotal) * 100) : 0;

  return (
    <div className="relative z-10 pt-[72px] min-h-screen bg-background">
      <div className="bg-navy text-white py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <span className="overline text-gold">{t("dash.welcome")}, {user?.name}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tight mt-2">{t("inv.title")}</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <Tabs defaultValue="project">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="project" data-testid="tab-project"><FileText size={16} className="me-2" /> {t("inv.tab_project")}</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments"><CalendarCheck size={16} className="me-2" /> {t("inv.tab_appointments")}</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts"><BellRinging size={16} className="me-2" /> {t("inv.tab_alerts")}</TabsTrigger>
            <TabsTrigger value="favorites" data-testid="tab-favorites"><Heart size={16} className="me-2" /> {t("inv.tab_favorites")}</TabsTrigger>
            <TabsTrigger value="request" data-testid="tab-request"><PaperPlaneTilt size={16} className="me-2" /> {t("inv.tab_request")}</TabsTrigger>
          </TabsList>

          {/* PROJECT / DOSSIER */}
          <TabsContent value="project" className="mt-8">
            {!dossier ? (
              <div className="grid md:grid-cols-3 gap-5">
                {PROJECTS.map((p) => (
                  <button key={p.key} data-testid={`project-${p.key}`} onClick={() => chooseProject(p.key)} className="text-start bg-white border border-navy/10 hover:border-gold p-6 rounded-2xl transition-colors">
                    <p.icon size={34} weight="light" className="text-gold mb-4" />
                    <h3 className="font-display text-xl text-navy mb-2">{t(`inv.${p.tk}_t`)}</h3>
                    <p className="text-sm text-muted-foreground">{t(`inv.${p.tk}_d`)}</p>
                  </button>
                ))}
              </div>
            ) : dossier.project_type === "financing" ? (
              <div className="bg-white border border-navy/10 rounded-2xl p-6 sm:p-8">
                <div className="flex justify-between items-center flex-wrap gap-3 mb-6">
                  <div>
                    <h2 className="font-display text-2xl text-navy">{t("inv.dossier")}</h2>
                    <span className={`text-xs font-semibold uppercase tracking-wider ${dossier.status === "approved" ? "text-green-600" : dossier.status === "rejected" ? "text-red-600" : "text-gold"}`}>{t(`st.${dossier.status}`)}</span>
                  </div>
                  <button onClick={() => setDossier(null)} className="text-xs text-muted-foreground hover:text-navy underline" data-testid="change-project">{t("inv.change")}</button>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">{t("inv.progress")}</span><span className="font-semibold text-navy">{docsUploaded}/{docsTotal} · {progress}%</span></div>
                  <Progress value={progress} className="h-2" />
                </div>

                <div className="space-y-3">
                  {dossier.documents.map((doc) => (
                    <div key={doc.key} data-testid={`doc-${doc.key}`} className="flex items-center gap-3 border border-navy/10 rounded-xl p-4">
                      {doc.file_id ? <CheckCircle size={22} weight="fill" className="text-green-600 shrink-0" /> : <Clock size={22} weight="light" className="text-muted-foreground shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-navy font-medium text-sm">{doc.label?.[lang] || doc.label?.fr || doc.label}</div>
                        {doc.filename && <div className="text-xs text-muted-foreground truncate">{doc.filename}</div>}
                      </div>
                      <input ref={(el) => (fileRefs.current[doc.key] = el)} type="file" className="hidden" data-testid={`file-input-${doc.key}`} onChange={(e) => e.target.files[0] && uploadDoc(doc.key, e.target.files[0])} />
                      <button onClick={() => fileRefs.current[doc.key]?.click()} className="inline-flex items-center gap-2 text-sm font-medium text-navy border border-navy/20 rounded-full px-4 py-2 hover:border-gold transition-colors">
                        <UploadSimple size={16} /> {doc.file_id ? t("inv.replace") : t("inv.upload")}
                      </button>
                    </div>
                  ))}
                </div>

                {dossier.status === "draft" && (
                  <button data-testid="submit-dossier" onClick={submitDossier} disabled={progress < 100} className="mt-6 inline-flex items-center gap-2 bg-navy text-white font-semibold px-6 py-3 rounded-full hover:bg-[#254B75] transition-colors disabled:opacity-50">
                    <PaperPlaneTilt size={18} /> {t("inv.submit")}
                  </button>
                )}
                {dossier.bank_note && <p className="mt-4 text-sm bg-gold/10 border border-gold/30 rounded-xl p-4 text-navy">{t("inv.bank_note")} {dossier.bank_note}</p>}
              </div>
            ) : (
              <div className="bg-white border border-navy/10 rounded-2xl p-6 sm:p-8">
                <h2 className="font-display text-2xl text-navy mb-1">{dossier.project_type === "implantation" ? t("inv.implantation") : t("inv.placement")}</h2>
                <p className="text-muted-foreground text-sm mb-6">{t("inv.appt_intro")}</p>
                <h3 className="font-medium text-navy mb-3">{t("inv.appt_slots")}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {slots.slice(0, 12).map((s) => (
                    <button key={s.id} data-testid={`slot-${s.id}`} onClick={() => book(s, dossier.project_type)} className="border border-navy/15 rounded-xl p-3 text-start hover:border-gold transition-colors">
                      <div className="text-sm font-medium text-navy">{new Date(s.start).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</div>
                      <div className="text-xs text-muted-foreground">{new Date(s.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    </button>
                  ))}
                  {slots.length === 0 && <div className="text-sm text-muted-foreground">{t("inv.no_slots")}</div>}
                </div>
                <button onClick={() => setDossier(null)} className="mt-6 text-xs text-muted-foreground underline hover:text-navy">{t("inv.change")}</button>
              </div>
            )}
          </TabsContent>

          {/* APPOINTMENTS */}
          <TabsContent value="appointments" className="mt-8">
            {appointments.length === 0 ? (
              <div data-testid="dash-no-appt" className="bg-white border border-dashed border-navy/20 rounded-2xl p-10 text-center text-muted-foreground">{t("inv.no_appt")}</div>
            ) : (
              <div className="space-y-3">
                {appointments.map((a) => (
                  <div key={a.id} className="bg-white border border-navy/10 rounded-xl p-4 flex items-center gap-4">
                    <CalendarCheck size={26} weight="light" className="text-gold" />
                    <div className="flex-1">
                      <div className="font-medium text-navy">{new Date(a.start).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</div>
                      <div className="text-xs text-muted-foreground">Sujet : {a.subject} · avec {a.expert_name || "Service client"}</div>
                    </div>
                    <span className="text-xs font-semibold text-green-600 uppercase">{a.status}</span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ALERTS */}
          <TabsContent value="alerts" className="mt-8">
            <AlertsPanel alerts={alerts} reload={load} />
          </TabsContent>

          {/* FAVORITES */}
          <TabsContent value="favorites" className="mt-8">
            {favorites.length === 0 ? (
              <div className="bg-white border border-dashed border-navy/20 rounded-2xl p-10 text-center text-muted-foreground">{t("inv.no_fav")}</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{favorites.map((p) => <PropertyCard key={p.id} property={p} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="request" className="mt-8">
            <RequestPanel user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const REQ_TYPES = ["residence", "individual", "commercial", "office", "logistics"];

const RequestPanel = ({ user }) => {
  const { t, lang } = useT();
  const [form, setForm] = useState({ type: "residence", city: "", budget: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.message.trim()) { toast.error("…"); return; }
    setSending(true);
    try {
      const msg = `[${t(`types.${form.type}`)}] ${form.city ? "Ville: " + form.city + ". " : ""}${form.budget ? "Budget max: " + form.budget + " MAD. " : ""}\n${form.message}`;
      await api.post("/contact", {
        name: user?.name || "Investisseur", email: user?.email || "n/a@yadimmo.com",
        phone: user?.phone || "", message: msg, subject: "property_request",
      });
      toast.success(t("req.sent"));
      setForm({ type: "residence", city: "", budget: "", message: "" });
    } catch (e) {
      toast.error("Erreur");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-6 max-w-2xl" data-testid="request-panel">
      <h3 className="font-display text-xl text-navy mb-1">{t("req.title")}</h3>
      <p className="text-sm text-muted-foreground mb-5">{t("req.sub")}</p>
      <div className="space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="request-type"><SelectValue /></SelectTrigger>
            <SelectContent>{REQ_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`types.${tp}`)}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="request-city" placeholder={t("req.city")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <Input data-testid="request-budget" type="number" placeholder={t("req.budget")} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
        </div>
        <textarea data-testid="request-message" rows={4} placeholder={t("req.msg")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-navy/15 rounded-xl p-3 text-sm" />
        <button data-testid="request-submit" onClick={submit} disabled={sending} className="w-full bg-navy text-white font-semibold py-3 rounded-full hover:bg-[#254B75] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <PaperPlaneTilt size={18} /> {t("req.submit")}
        </button>
      </div>
    </div>
  );
};

const TYPES = ["any", "residence", "individual", "commercial", "office", "logistics"];

const AlertsPanel = ({ alerts, reload }) => {
  const { t } = useT();
  const [form, setForm] = useState({ type: "any", city: "", min_price: "", max_price: "", note: "" });

  const create = async () => {
    await api.post("/alerts", {
      type: form.type, city: form.city || "any",
      min_price: Number(form.min_price) || 0, max_price: Number(form.max_price) || 0, note: form.note,
    });
    toast.success(t("alerts.created"));
    setForm({ type: "any", city: "", min_price: "", max_price: "", note: "" });
    reload();
  };
  const remove = async (id) => { await api.delete(`/alerts/${id}`); reload(); };

  return (
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
      <div className="bg-white border border-navy/10 rounded-2xl p-6 h-fit">
        <h3 className="font-display text-xl text-navy mb-4">{t("alerts.create")}</h3>
        <div className="space-y-3">
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="alert-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp === "any" ? t("alerts.type_any") : t(`types.${tp}`)}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="alert-city" placeholder={t("alerts.city_ph")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="alert-min" type="number" placeholder={t("alerts.min_ph")} value={form.min_price} onChange={(e) => setForm({ ...form, min_price: e.target.value })} />
            <Input data-testid="alert-max" type="number" placeholder={t("alerts.max_ph")} value={form.max_price} onChange={(e) => setForm({ ...form, max_price: e.target.value })} />
          </div>
          <Input placeholder={t("alerts.note_ph")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <button data-testid="alert-create" onClick={create} className="w-full bg-navy text-white font-semibold py-3 rounded-full hover:bg-[#254B75] transition-colors">{t("alerts.create_btn")}</button>
        </div>
      </div>
      <div>
        {alerts.length === 0 ? (
          <div className="bg-white border border-dashed border-navy/20 rounded-2xl p-10 text-center text-muted-foreground">{t("alerts.empty")}</div>
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-${a.id}`} className="bg-white border border-navy/10 rounded-xl p-4 flex items-center gap-4">
                <BellRinging size={24} weight="light" className="text-gold" />
                <div className="flex-1">
                  <div className="font-medium text-navy">{a.type === "any" ? t("alerts.type_any") : t(`types.${a.type}`)} · {a.city !== "any" ? a.city : t("alerts.all_cities")}</div>
                  <div className="text-xs text-muted-foreground">
                    {a.min_price ? `${formatMAD(a.min_price)} ` : ""}{a.max_price ? `→ ${formatMAD(a.max_price)} MAD` : ""} {a.note ? `· ${a.note}` : ""}
                  </div>
                </div>
                <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-red-500"><Trash size={18} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorDashboard;
